import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { IoCloseSharp } from 'react-icons/io5';
import { appendLoanNodeToPipeline, editLoanNode, getGlobalParams } from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { validateNodeName } from '../../../name_validation';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';
import './LoanConfigModal.css';

// Set the app element for accessibility
try {
  Modal.setAppElement('#root');
} catch (error) {
  console.warn('Could not set app element for Modal:', error);
  // This prevents the app from crashing if the root element isn't available
}

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.375rem',
    padding: '1.5rem',
    maxWidth: '500px',
    width: '90%',
    position: 'relative',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999, // Higher z-index to ensure it appears on top
  }
};

const LoanConfigModal = ({ isOpen, onClose, onConfigured, initialData = {} }) => {
  // Simple state initialization
  const [nodeName, setNodeName] = useState(initialData.name || '');
  const [nodeNameError, setNodeNameError] = useState('');
  const [loanPerc, setLoanPerc] = useState(initialData.loanPerc || '');
  const [annualRate, setAnnualRate] = useState(initialData.annualRate || '');
  const [tenureYears, setTenureYears] = useState(initialData.tenureYears || '');
  const [moratoriumYears, setMoratoriumYears] = useState(initialData.moratoriumYears || '0');
  const { session } = usePipeline(); // Use the context to get the session
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingGlobal, setLoadingGlobal] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Determine if we're in edit mode (has a name AND isConfigured is true)
  const isEditing = !!initialData.name && initialData.isConfigured === true;
  
  // Keep track of whether the modal has been opened
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Update state only when the modal is opened or initialData changes before user interaction
  useEffect(() => {
    if (isOpen) {
      // Only initialize values if this is the first time opening or if the modal was closed and reopened
      if (!hasInitialized) {
        setNodeName(initialData.name || '');
        setLoanPerc(initialData.loanPerc || '');
        setAnnualRate(initialData.annualRate || '');
        setTenureYears(initialData.tenureYears || '');
        setMoratoriumYears(initialData.moratoriumYears || '0');
        
        setHasInitialized(true);
        
        console.log('Modal opened with data:', {
          initialData,
          isEditing: initialData.isEditing || false
        });
      }
    } else {
      // Reset the initialization flag when the modal is closed
      setHasInitialized(false);
    }
  }, [isOpen, initialData, hasInitialized]);

  // Load defaults from global store for convenience on new nodes
  useEffect(() => {
    const loadGlobal = async () => {
      if (!isOpen) return;
      if (!session?.pipeline_name || session.pipeline_name === 'Unsaved_Pipeline') return;
      setLoadingGlobal(true);
      try {
        const gp = await getGlobalParams(session.pipeline_name);
        if (gp?.loan_perc != null) setLoanPerc(gp.loan_perc);
        if (gp?.annual_rate != null) setAnnualRate(gp.annual_rate);
        if (gp?.tenure_years != null) setTenureYears(gp.tenure_years);
        if (gp?.moratorium_years != null) setMoratoriumYears(gp.moratorium_years);
      } catch (e) {
        // ignore failures, keep local
      } finally {
        setLoadingGlobal(false);
      }
    };
    loadGlobal();
  }, [isOpen, session?.pipeline_name]);
  
  // Validate node name
  useEffect(() => {
    // Always validate the node name, even if it's empty
    const validation = validateNodeName(nodeName);
    setNodeNameError(validation.isValid ? '' : validation.errorMessage);
  }, [nodeName]);

  const handleConfigure = async () => {
    try {
      setSubmitError(null);
      // Validate node name using the validation function
      const nameValidation = validateNodeName(nodeName);
      if (!nameValidation.isValid) {
        setNodeNameError(nameValidation.errorMessage);
        // No need for alert as the button will be disabled
        return;
      }

      // Make sure we have valid loan parameters
      if (!loanPerc || isNaN(loanPerc)) {
        setSubmitError({ title: 'Invalid loan %', message: 'Please enter a valid loan percentage (numeric value).' });
        return;
      }

      if (!annualRate || isNaN(annualRate)) {
        setSubmitError({ title: 'Invalid annual rate', message: 'Please enter a valid annual rate (numeric value).' });
        return;
      }

      if (!tenureYears || isNaN(tenureYears) || parseInt(tenureYears) <= 0) {
        setSubmitError({ title: 'Invalid tenure', message: 'Please enter a valid tenure in years (positive integer).' });
        return;
      }

      if (moratoriumYears === '' || isNaN(moratoriumYears) || parseInt(moratoriumYears) < 0) {
        setSubmitError({ title: 'Invalid moratorium', message: 'Please enter a valid moratorium period in years (non-negative integer).' });
        return;
      }
      
      // Get the position from initialData
      const position = initialData.position || { x: 0, y: 0 };
      
      // Set submitting state to show loading indicator
      setIsSubmitting(true);
      
      console.log("Configuration mode:", {
        isEditing,
        name: initialData.name,
        isConfigured: initialData.isConfigured
      });
      
      // Make the API call if we have a valid pipeline name
      if (session.pipeline_name !== "Unsaved_Pipeline") {
        try {
          if (isEditing) {
            // We're editing an existing node
            console.log(`Calling API to edit loan node: ${initialData.name} in pipeline: ${session.pipeline_name}`);
            console.log(`New parameters - Loan %: ${loanPerc}, Rate: ${annualRate}, Tenure: ${tenureYears}, Moratorium: ${moratoriumYears}`);
            
            await editLoanNode(
              session.pipeline_name,
              initialData.name,  // Use the original name
              parseFloat(loanPerc),
              parseFloat(annualRate),
              parseInt(tenureYears),
              parseInt(moratoriumYears)
            );
            
            console.log(`Successfully edited loan node ${initialData.name}`);
          } else {
            // We're creating a new node
            console.log(`Calling API to append loan node: ${nodeName} to pipeline: ${session.pipeline_name}`);
            console.log(`Parameters - Loan %: ${loanPerc}, Rate: ${annualRate}, Tenure: ${tenureYears}, Moratorium: ${moratoriumYears}, Position: (${position.x}, ${position.y})`);
            
            await appendLoanNodeToPipeline(
              session.pipeline_name,
              nodeName,
              parseFloat(loanPerc),
              parseFloat(annualRate),
              parseInt(tenureYears),
              parseInt(moratoriumYears),
              position.x,
              position.y
            );
            
            console.log(`Successfully added loan node ${nodeName} to backend`);
          }
        } catch (apiError) {
          console.error(`Error calling ${isEditing ? 'editLoanNode' : 'appendLoanNodeToPipeline'}:`, apiError);
          const info = notifyError(apiError, {
            operation: isEditing ? OP.node.editLoan : OP.node.appendLoan,
            showToast: false,
          });
          setSubmitError(info);
          // Don't continue with UI updates if API call fails
          setIsSubmitting(false);
          return;
        }
      } else {
        console.log(`Skipping API call for unsaved pipeline`);
      }
      
      // Call the callback to update the UI
      onConfigured({
        name: isEditing ? initialData.name : nodeName, // Keep original name in edit mode
        loanPerc: parseFloat(loanPerc),
        annualRate: parseFloat(annualRate),
        tenureYears: parseInt(tenureYears),
        moratoriumYears: parseInt(moratoriumYears)
      });
      
      // Reset submitting state
      setIsSubmitting(false);
      
      // Note: We don't close the modal here as it will be closed by the parent component
    } catch (error) {
      console.error('Error configuring loan node:', error);
      const info = notifyError(error, { showToast: false });
      setSubmitError(info);
      setIsSubmitting(false);
      return; // Exit early on error
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Loan Configuration"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      ariaHideApp={false} // Prevents warning about appElement
    >
      <div className="loan-modal">
        <button onClick={onClose} className="loan-modal-close">
          <IoCloseSharp size={24} />
        </button>
        
        <h2 className="loan-modal-title">
          {isEditing ? "Edit Loan Parameters" : "Configure New Loan Node"}
        </h2>

        {submitError && (
          <div
            role="alert"
            className="loan-modal-error"
            style={{
              marginTop: '0.75rem',
              marginBottom: '0.75rem',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#b91c1c',
              fontSize: '0.95rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{submitError.title}</div>
            <div>{submitError.message}</div>
          </div>
        )}
        
        <div className="loan-modal-field">
          <label className="loan-modal-label">
            Node Name: <span className="required">*</span>
          </label>
          {isEditing ? (
            <input
              type="text"
              value={nodeName}
              className="loan-modal-input"
              disabled={true}
              title="Node name cannot be changed in edit mode"
            />
          ) : (
            <input
              type="text"
              value={nodeName}
              onChange={(e) => setNodeName(e.target.value)}
              onBlur={() => {
                // Validate again on blur to ensure we show errors when user leaves the field
                const validation = validateNodeName(nodeName);
                setNodeNameError(validation.isValid ? '' : validation.errorMessage);
              }}
              className={`loan-modal-input ${nodeNameError ? 'error' : ''}`}
              placeholder="Enter node name (letters, digits, hyphens, underscores, periods)"
              aria-invalid={!!nodeNameError}
              aria-describedby={nodeNameError ? "node-name-error" : undefined}
            />
          )}
          {!isEditing && nodeNameError && (
            <div className="loan-modal-error" id="node-name-error" role="alert">{nodeNameError}</div>
          )}
          {!isEditing && !nodeNameError && (
            <div className="loan-modal-hint">
              Valid characters: letters, digits, hyphens (-), underscores (_), periods (.)
            </div>
          )}
        </div>
        
        <div className="loan-modal-field">
          <label className="loan-modal-label">
            Loan Percentage (% of Capex): <span className="required">*</span>
          </label>
          <input
            type="number"
            value={loanPerc}
            onChange={(e) => setLoanPerc(e.target.value)}
            className="loan-modal-input"
            placeholder="Enter loan percentage (e.g. 80)"
            step="0.01"
            min="0"
            max="100"
          />
        </div>

        <div className="loan-modal-field">
          <label className="loan-modal-label">
            Annual Interest Rate (%): <span className="required">*</span>
          </label>
          <input
            type="number"
            value={annualRate}
            onChange={(e) => setAnnualRate(e.target.value)}
            className="loan-modal-input"
            placeholder="Enter annual rate (e.g. 9)"
            step="0.01"
            min="0"
            max="100"
          />
        </div>

        <div className="loan-modal-field">
          <label className="loan-modal-label">
            Tenure (Years): <span className="required">*</span>
          </label>
          <input
            type="number"
            value={tenureYears}
            onChange={(e) => setTenureYears(e.target.value)}
            className="loan-modal-input"
            placeholder="Enter tenure in years (e.g. 9)"
            step="1"
            min="1"
          />
        </div>

        <div className="loan-modal-field">
          <label className="loan-modal-label">
            Moratorium Period (Years): <span className="required">*</span>
          </label>
          <input
            type="number"
            value={moratoriumYears}
            onChange={(e) => setMoratoriumYears(e.target.value)}
            className="loan-modal-input"
            placeholder="Enter moratorium period in years (e.g. 1)"
            step="1"
            min="0"
          />
        </div>
        
        <div className="loan-modal-button-container">
          <button 
            className="loan-modal-configure-button"
            onClick={handleConfigure}
            disabled={
              isSubmitting || 
              (!isEditing && (!!nodeNameError || !nodeName)) || 
              !loanPerc || isNaN(loanPerc) ||
              !annualRate || isNaN(annualRate) ||
              !tenureYears || isNaN(tenureYears) ||
              moratoriumYears === '' || isNaN(moratoriumYears)
            }
            title={
              nodeNameError ? nodeNameError : 
              (!nodeName && !isEditing) ? 'Node name is required' : 
              !loanPerc || isNaN(loanPerc) ? 'Valid loan percentage is required' : 
              !annualRate || isNaN(annualRate) ? 'Valid annual rate is required' :
              !tenureYears || isNaN(tenureYears) ? 'Valid tenure is required' :
              moratoriumYears === '' || isNaN(moratoriumYears) ? 'Valid moratorium period is required' : ''
            }
          >
            {isSubmitting ? 'Processing...' : 
              isEditing ? 'Save Changes' : 'Configure'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LoanConfigModal;